import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useSites } from '../hooks/useSites'
import { canManageSites } from '../lib/roles'
import { SiteCard } from '../components/site/SiteCard'
import { AddSiteDialog } from '../components/site/AddSiteDialog'
import { GridSkeleton } from '../components/ui/Skeleton'

export function DashboardPage() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const { sites, cameraCounts, loading, createSite } = useSites(profile?.org_id)
  const [showAdd, setShowAdd] = useState(false)
  const [activeGroup, setActiveGroup] = useState<string | null>(null)

  const groups = useMemo(() => {
    const set = new Set(sites.map(s => s.group_name).filter(Boolean) as string[])
    return Array.from(set).sort()
  }, [sites])

  const filtered = activeGroup
    ? sites.filter(s => s.group_name === activeGroup)
    : sites

  if (loading) {
    return <GridSkeleton count={6} />
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Dashboard</h1>
          <p className="text-gray-500 text-sm mt-1">
            {profile?.role === 'rv_admin'
              ? 'All sites across all organizations'
              : `Sites for ${profile?.org_name ?? 'your organization'}`}
          </p>
        </div>
        {profile && canManageSites(profile.role) && (
          <button
            onClick={() => setShowAdd(true)}
            className="bg-brand hover:bg-brand-dark text-white text-sm font-medium px-4 py-2 rounded-md transition-colors"
          >
            Add Site
          </button>
        )}
      </div>

      {/* Group filter tabs */}
      {groups.length > 0 && (
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <button
            onClick={() => setActiveGroup(null)}
            className={`text-xs px-3 py-1.5 rounded-md transition-colors ${
              !activeGroup ? 'bg-brand text-white' : 'bg-gray-100 text-gray-500 hover:text-gray-900'
            }`}
          >
            All ({sites.length})
          </button>
          {groups.map(g => (
            <button
              key={g}
              onClick={() => setActiveGroup(g)}
              className={`text-xs px-3 py-1.5 rounded-md transition-colors ${
                activeGroup === g ? 'bg-brand text-white' : 'bg-gray-100 text-gray-500 hover:text-gray-900'
              }`}
            >
              {g} ({sites.filter(s => s.group_name === g).length})
            </button>
          ))}
          {activeGroup && (
            <button
              onClick={() => navigate(`/viewer?group=${encodeURIComponent(activeGroup)}`)}
              className="text-xs px-3 py-1.5 rounded-md bg-brand/10 text-brand hover:bg-brand hover:text-white transition-colors ml-1"
            >
              Open Group Viewer
            </button>
          )}
        </div>
      )}

      {filtered.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map(site => (
            <SiteCard key={site.id} site={site} cameraCount={cameraCounts[site.id] ?? 0} />
          ))}
        </div>
      ) : (
        <div className="bg-card border border-card-border rounded-lg p-8 text-center text-gray-500">
          <p className="text-lg mb-2">No sites yet</p>
          <p className="text-sm">Click "Add Site" to add your first location.</p>
        </div>
      )}

      <AddSiteDialog
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onCreated={async (site) => { await createSite(site) }}
      />
    </div>
  )
}
